/**
 * AtendimentoFilaPage — domínio "Atendimento & fila" (`/admin/atendimento`), Admin
 * Hub T9. Terceira das 5 páginas de domínio (T7-T11); carrega o único teste de
 * REGRESSÃO DE SEGURANÇA do plano inteiro (`#fila-pos-transferencia`).
 *
 * `canManage` (`tenant-config-manage`) é resolvido AQUI, uma vez, e passado aos 3
 * componentes que escrevem — mesmo contrato que `OrganizacaoPage`/`IaCondutaPage`
 * já usam (medido em context.md). Cada seção segue um contrato de escrita distinto,
 * todos já existentes (nenhum caminho novo criado nesta task):
 *
 *   - `#perfil-de-operacao` (`OperationProfileTab`) — AÇÃO com confirmação
 *     vinculante (digitar o nome do perfil), não formulário. NÃO registra na
 *     `StickySaveBar` — mantém botão + `Modal` de confirmação próprios, intactos.
 *     Aplicar um preset troca config em massa + evento auditado `high` (docblock
 *     do componente); CLAUDE.md §10 lista esta classe de ação como human-pause
 *     território. Nada aqui dá a ela um caminho para a barra agregada.
 *   - `#fila-pos-transferencia` (`QueueLimboTab`) — `omitWhenBarred: true` no
 *     catálogo, capability `ai-queue-receptionist`. Reproduz o fail-closed de
 *     `ConfigTenantPage.tsx:98` (`visibleTabs = TABS.filter((t) => t.id !==
 *     'queue-limbo' || canReceptionist)`): cap ausente ⇒ a seção fica AUSENTE —
 *     nem corpo (`DomainSection` retorna `null`), nem link no rail (`DomainPage`
 *     filtra `itens` pela mesma fonte de capabilities antes de montar a `<nav>`,
 *     fix round 2 da T4). Mecanismo genérico já coberto por
 *     `DomainPage.spec.tsx`/`DomainSection` (T4, com este MESMO item real do
 *     catálogo); o teste desta página confirma que a COMPOSIÇÃO real (com
 *     `QueueLimboTab` como filho) preserva a ausência nas duas superfícies —
 *     é o teste de regressão de segurança que a T9 carrega.
 *   - `#notificacoes-do-operador` (`OperatorShiftTab`) — dirty-diff real,
 *     registra na `StickySaveBar` via `useSectionSave`. Migrado nesta task:
 *     trocou `update.mutate` por `update.mutateAsync` (o `save()` da registration
 *     devolve `Promise<void>`) e o `<header>` próprio saiu, mesmo movimento que
 *     `GeneralTab` (T7) e `ConductTab`/`SilentTab`/`BehavioralTab` (T8) já fizeram.
 *   - `#notificacoes` — só `DomainSection` com `soon` (catálogo), sem filho: a
 *     sub-tela do mockup ainda não tem backend (honest-defer).
 *
 * Fidelidade — prancha "06 Dominio Atendimento e fila.dc.html", variante 6a
 * (único desenho da prancha; não há variantes alternativas como na 02). Duas
 * divergências DECLARADAS:
 *
 *   1. **Cabeçalhos duplicados em `#perfil-de-operacao` e `#fila-pos-transferencia`.**
 *      A prancha desenha CADA seção com um único `.sec-hd` (1 título). Aqui,
 *      `DomainSection` desenha o título/descrição do catálogo (`item.label`/
 *      `item.efeito`) E `OperationProfileTab`/`QueueLimboTab` mantêm o `<header>`
 *      próprio deles (h2 + parágrafo) — layering visual que a prancha não mostra.
 *      Isto NÃO foi corrigido nesta task: o dispatch da T9 instrui explicitamente
 *      "leave all of it intact" para `OperationProfileTab` (o estado de
 *      confirmação vinculante) e não pede a remoção do cabeçalho de nenhum dos
 *      dois — ao contrário de `OperatorShiftTab`, onde a conversão à
 *      `StickySaveBar` inclui essa remoção como parte do MESMO padrão que T7/T8
 *      já aplicaram. Diferença adicional que reforça a cautela: o texto do
 *      cabeçalho próprio de `OperationProfileTab` ("Um perfil aplica defaults
 *      coerentes de privacidade e distribuição de fila. Trocar de perfil é um
 *      evento auditado.") é IDÊNTICO ao `sec-desc` da prancha para esta seção —
 *      é o catálogo (`item.efeito`: "Preset que define quanto a IA conduz
 *      sozinha — aplicar muda a operação inteira.") que diverge da prancha, não
 *      o componente. Remover o header sem antes corrigir `catalog.ts` teria
 *      trocado o texto CERTO (fiel à prancha) pelo texto DIVERGENTE — o oposto
 *      do que se quer. Fica para o TL decidir: corrigir `catalog.ts` e então
 *      remover os 2 headers (mesma dívida que a T8 já pagou para
 *      `acompanhamento-silencioso`), ou aceitar a duplicação.
 *   2. A busca "Buscar nesta página…" do cabeçalho da prancha não existe — é
 *      parte do layout de `DomainPage` (T4), fora do escopo desta task (mesma
 *      nota que T7/T8 já registraram para as pranchas 05/02).
 *
 * Divergência de CONTEÚDO (não de layout) em `#fila-pos-transferencia`: a
 * prancha desenha 3 campos IMAGINADOS ("Limite de pessoas na fila", "Modo de
 * promoção da fila", "Exigir aprovação para entrar na fila") que não existem em
 * nenhum lugar do backend — `QueueLimboTab` (o componente REAL mapeado a este
 * id pelo dispatch da T9) expõe os 2 toggles reais (`ackEnabled`/
 * `receptionistEnabled`) + 4 campos numéricos de rate-limit do
 * `escalationLimboPolicy`. Mesma classe de situação que a T8 encontrou em
 * `acompanhamento-silencioso` (prancha/mockup descrevendo uma feature que o
 * componente real não implementa) — mas ali o CATÁLOGO estava errado (citava a
 * capability de outra feature); aqui o catálogo já está correto (`efeito`:
 * "Política do que a IA faz com quem espera humano após a transferência" —
 * descreve fielmente ack+recepcionista), só a PRANCHA imaginou campos que o
 * backend não tem. Resolvido a favor do componente real (R15 — não fabricar UI
 * para campo inexistente), seguindo a mesma instrução do dispatch de reusar o
 * componente REAL em vez de reconstruir da prancha (precedente: `BrandingSection`
 * na T7, `ModelosSection` na T8).
 */
import { useCapability } from '../../../../hooks/use-capability';
import { OperationProfileTab } from '../../config-tenant/OperationProfileTab';
import { OperatorShiftTab } from '../../config-tenant/OperatorShiftTab';
import { QueueLimboTab } from '../../config-tenant/QueueLimboTab';
import { CONFIG_CATALOG } from '../catalog';
import { DomainPage } from '../DomainPage';
import { DomainSection } from '../DomainSection';
import { DomainSaveProvider } from '../save-bar-context';
import { StickySaveBar } from '../StickySaveBar';

function item(id: string) {
  const found = CONFIG_CATALOG.find((i) => i.id === id);
  if (found === undefined) throw new Error(`Item de catálogo ausente: ${id}`);
  return found;
}

export function AtendimentoFilaPage(): JSX.Element {
  const canManage = useCapability('tenant-config-manage');

  return (
    <DomainSaveProvider>
      <DomainPage dominio="atendimento">
        <DomainSection item={item('perfil-de-operacao')}>
          <OperationProfileTab canManage={canManage} />
        </DomainSection>
        <DomainSection item={item('fila-pos-transferencia')}>
          <QueueLimboTab canManage={canManage} />
        </DomainSection>
        <DomainSection item={item('notificacoes-do-operador')}>
          <OperatorShiftTab canManage={canManage} />
        </DomainSection>
        <DomainSection item={item('notificacoes')}>{null}</DomainSection>
        <StickySaveBar />
      </DomainPage>
    </DomainSaveProvider>
  );
}
